import { createContext, CSSProperties, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Toast, ToastProps } from 'shared/components/Toast';

type ToastInput = Omit<ToastProps, 'onClose'>;

interface ToastContextValue {
  showToast: (toast: ToastInput) => void;
}

interface ActiveToast extends ToastInput {
  id: number;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DISMISS_MS = 3500;
const ENTRANCE_DURATION_MS = 300;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((toast: ToastInput) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-6 right-6 z-[300] flex flex-col gap-4 max-w-sm w-[calc(100%-3rem)] sm:w-auto pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ActiveToast; onDismiss: () => void }) {
  const [entered, setEntered] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setEntered(true);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(onDismiss, DISMISS_MS);
    return () => clearTimeout(timer);
  }, [paused, onDismiss]);

  const style: CSSProperties = {
    opacity: entered ? 1 : 0,
    transform: entered ? 'translateX(0)' : 'translateX(1rem)',
    transitionProperty: 'opacity, transform',
    transitionDuration: `${ENTRANCE_DURATION_MS}ms`,
    transitionTimingFunction: 'ease-out',
  };

  return (
    <div
      className="pointer-events-auto"
      style={style}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Toast
        variant={toast.variant}
        title={toast.title}
        message={toast.message}
        onClose={onDismiss}
      />
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
